import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
  Flex,
  Text,
  Box,
  FormLabel,
  FormControl,
  Tooltip,
  Badge,
  Grid
} from '@chakra-ui/react';
import {
  Button,
  FormFeedback,
  Input,
  Select,
  TextArea,
  useToast,
  Switch,
  Checkbox,
  Modal,
  TagInput,
  Link
} from '@morressier/alembic';
import {
  Control,
  Controller,
  FieldErrors,
  useForm,
  useWatch
} from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Overlay } from 'src/components/Overlay';
import { Editor } from '@tinymce/tinymce-react';
import { FieldLabel } from 'src/components/Field';
import {
  createEmailTemplate,
  editEmailTemplateById,
  getEmailEventsWithSubEvents,
  getReminderConfigsByOrgId,
  getReminderConfigsByOrgIdAndJournalId,
  getTemplateByTemplateId,
  ICreateTemplatePayload,
  IUpdateTemplatePayload,
  validateEmailTemplate,
  listEmailTemplateCategories,
  getEmailTemplatePreview,
  validateDefaultTemplate,
  IValidateDefaultTemplate
} from 'src/api/email-service';
import {
  EmailScope,
  EventName,
  IEmailContentType,
  ITemplate,
  TemplateCategoryDoc,
  TemplateValidationResult
} from '@publishing-engine/domain/models/email-templates';
import { faCircleCheck, faSliders } from '@fortawesome/pro-regular-svg-icons';
import MultiSelect from 'src/components/MultiSelect';
import ComposableMultiSelect, {
  MultiSelectOption
} from 'src/components/FlexibleMultiSelect';
import TemplateVariablesList from 'src/components/TemplateVariablesList';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchOrganizationJournals } from 'src/api/queries';
import dateFormat from 'dateformat';
import TestEmailSent from './TestEmailSent';
import { IRemindersJournalConfigRouteResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { convertNumberToOrdinal } from '../../../../../utils/number';
import AddReminderCycleModal, {
  IAddReminderCycleFormType
} from './AddReminderCycleModal';
import { TransitionWorkflowType } from '@publishing-engine/domain/validation';
import { useUpdateReminderConfigsByJournalId } from '../../../../../api/mutations';
import { listGlobalTemplatesByEvent } from '../../../../../api/email-service';
import { getErrorTitle } from './EditCategoryModal';
import { triggerTypeLabelMapping } from './TemplatePreview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FilterableSelect } from 'src/components/FilterableSelect';
import PreviewEmail from 'src/components/EmailModule/PreviewEmail';
import DefaultEmailTemplateModal from './DefaultEmailTemplateModal';
import { defaultTemplatenameAndEvent } from './EmailTemplatesTable';
import DeleteTemplateModal from './DeleteTemplateModal';
import StatusActionModal from './StatusActionModal';
import { TemplateFlowSuccessLayout } from './TemplateFlowSuccessLayout';
import { extractEmailsFromTags } from 'src/utils/normalizeEmailRecipients';

type Phase = 'sub' | 'resub' | 'rev';

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 100;

export interface TemplateCreate {
  name: string;
  to: { text: string }[];
  cc: { text: string }[];
  bcc: { text: string }[];
  subject: string;
  body: string;
  event: string;
  submissionContextId?: string[];
  customizable: boolean;
  category?: string | undefined;
  workspaceId?: string | undefined;
  description: string;
  active: boolean;
  trigger: string;
  phases?: Phase[];
  scope: EmailScope;
  default: boolean;
  state: string;
  reminderRound: string[];
  override?: string;
  triggerEmail?: string;
  daysOffset?: string[];
  tag?: string;
}

const defaultValues: TemplateCreate = {
  name: '',
  to: [],
  cc: [],
  bcc: [],
  subject: '',
  body: '',
  event: '',
  submissionContextId: [],
  customizable: false,
  category: '',
  workspaceId: '',
  description: '',
  active: true,
  default: false,
  state: '',
  reminderRound: [],
  trigger: 'SystemEvents',
  phases: ['sub', 'rev', 'resub'],
  scope: 'global',
  tag: ''
};

interface Props {
  onClose: () => void;
  isOpen: boolean;
  organizationId: string;
  templateId: string;
  onSuccess: () => void;
  isCreating?: boolean;
  categories?: TemplateCategoryDoc[];
  isEmailTemplateClone?: boolean;
  setIsClone?: React.Dispatch<React.SetStateAction<boolean>>;
}

const filterAndGetReminderRounds = (
  event?: string,
  selectedPhase?: string,
  reminderConfigs?: IRemindersJournalConfigRouteResponse[] | null
): {
  reminderRounds: number[];
  reminderDays?: number[];
  reminderCycleRounds?: number[];
} => {
  const normalizedEvent = event?.toLowerCase();

  if (!event && !selectedPhase && !reminderConfigs?.length) {
    return { reminderRounds: [] };
  }

  const reminderConfig = reminderConfigs?.find(
    ({ type, phase }) =>
      normalizedEvent?.includes(type?.toLowerCase()) && phase === selectedPhase
  );

  return {
    reminderRounds: reminderConfig?.reminderRounds ?? [],
    reminderDays: reminderConfig?.reminderInDays ?? [],
    reminderCycleRounds: reminderConfig?.reminderRoundDays ?? []
  };
};

const FreshTestEmailContainer = ({
  control,
  organizationId
}: {
  control: Control<TemplateCreate>;
  organizationId: string;
}) => {
  const liveTemplateData = useWatch({ control }) as TemplateCreate;
  const { to, cc, bcc, ...restOfData } = liveTemplateData;

  const safeTemplate = {
    ...restOfData,
    subject: liveTemplateData.subject ?? '',
    body: liveTemplateData.body ?? '',
    recipients: {
      to: extractEmailsFromTags(to),
      cc: extractEmailsFromTags(cc),
      bcc: extractEmailsFromTags(bcc)
    }
  };

  return (
    <TestEmailSent template={safeTemplate} organizationId={organizationId} />
  );
};

const ManageCyclesButton = () => {
  return (
    <Flex paddingX={1} gap={1} alignItems="center">
      <Flex paddingX={1}>
        <FontAwesomeIcon icon={faSliders} />
      </Flex>
      <Text textStyle="headingItem">Manage Cycles</Text>
    </Flex>
  );
};

const EditTemplateModal = ({
  onClose,
  isOpen,
  organizationId,
  templateId,
  onSuccess,
  isCreating = false,
  categories = [],
  isEmailTemplateClone = false,
  setIsClone
}: Props) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);

  const [selectedSubmissionCtxId, setSelectedSubmissionCtxId] =
    useState<string>('');
  const [addNewReminderDaysModalEnabled, setAddNewReminderDaysModalEnabled] =
    useState<boolean>(false);

  const [isOpenStatusModal, setIsOpenStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<boolean | null>(null);

  // State for the Success Overlay
  const [createdTemplateInfo, setCreatedTemplateInfo] = useState<{
    name: string;
    templateId: string;
  } | null>(null);

  const [, setSearchParams] = useSearchParams();

  // State for default template confirmation modal
  const [
    showDefaultTemplateConfirmationModal,
    setShowDefaultTemplateConfirmationModal
  ] = useState(false);

  // state to set template name and event name to change default template
  const templateNameAndEvent = useRef<{ name: string; event: string }>(
    defaultTemplatenameAndEvent
  );

  // State to hold preview template
  const [previewTemplate, setPreviewTemplate] =
    useState<IEmailContentType | null>(null);

  // Preview modal state
  const [openPreviewEmail, setOpenPreviewEmail] = useState(false);
  const [isDeleteTemplateModalOpen, setIsDeleteTemplateModalOpen] =
    useState(false);

  const apiKeyTinymce = import.meta.env.VITE_TINY_MCE_CLIENT_KEY;
  const selfHostedTinymce = import.meta.env.VITE_SELF_HOSTED_TINY_MCE;

  // Fetch template details
  const { data: templateDetails, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['email-template-details', templateId, organizationId],
    queryFn: () => getTemplateByTemplateId(templateId, organizationId),
    enabled: isOpen && !!templateId && !isCreating
  });

  //This below method removed from the parent and called directly in the child to get the latest data from the api.
  const { data: categoriesData } = useQuery({
    queryKey: ['email-categories', { organizationId }],
    queryFn: () =>
      organizationId
        ? listEmailTemplateCategories(organizationId, { skip: 0, limit: 100 })
        : null
  });

  categories = categoriesData?.data || [];

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue,
    getValues,
    resetField,
    setError,
    unregister,
    clearErrors
  } = useForm<TemplateCreate>({
    defaultValues: defaultValues,
    shouldFocusError: false
  });

  const descriptionValue = watch('description');
  const triggerValue = watch('trigger');
  const eventValue = watch('event');
  const stateValue = watch('state');
  const scopeValue = watch('scope');
  const submissionContextIdValue = watch('submissionContextId');
  const customizable = watch('customizable');
  const selectedJournalId = submissionContextIdValue?.[0] ?? '';

  useEffect(() => {
    if (scopeValue !== 'journal' || triggerValue !== 'Reminder') {
      setSelectedSubmissionCtxId('');
      return;
    }

    setSelectedSubmissionCtxId(selectedJournalId);
  }, [scopeValue, triggerValue, selectedJournalId]);

  const { shortname = '' } = useParams();
  const { data } = useQuery({
    queryKey: ['organization-journals', { shortname }],
    queryFn: () =>
      fetchOrganizationJournals(shortname, DEFAULT_PAGE, DEFAULT_PAGE_SIZE)
  });
  const journalData = data?.collection || [];

  // use these configs for reminder templates
  const { data: reminderConfigsForOrganization } = useQuery({
    queryKey: ['reminder-emails-config', organizationId],
    queryFn: () => getReminderConfigsByOrgId(organizationId),
    enabled: isOpen
  });

  // use these configs for reminder templates
  const { data: reminderConfigsForJournal } = useQuery({
    queryKey: [
      'reminder-emails-config',
      organizationId,
      selectedSubmissionCtxId
    ],
    queryFn: () =>
      selectedSubmissionCtxId
        ? getReminderConfigsByOrgIdAndJournalId(
            organizationId,
            selectedSubmissionCtxId
          )
        : null,
    enabled: isOpen && !!selectedSubmissionCtxId
  });

  const reminderConfigs = selectedSubmissionCtxId
    ? reminderConfigsForJournal
    : reminderConfigsForOrganization;

  // Fetch events for dropdown in create modal
  const { data: eventsData } = useQuery({
    queryKey: ['email-events', { organizationId }],
    queryFn: () => getEmailEventsWithSubEvents(organizationId)
  });

  const overrideEventValue =
    triggerValue === 'AdhocEmailSent' ? triggerValue : eventValue;

  // Fetch global templates by selected event
  const { data: globalTemplates } = useQuery({
    queryKey: [
      'global-override-templates',
      { organizationId, eventValue: overrideEventValue, state: stateValue }
    ],
    queryFn: () =>
      listGlobalTemplatesByEvent(
        organizationId,
        overrideEventValue as string,
        stateValue
      ),
    enabled: scopeValue === 'journal' && !!overrideEventValue
  });

  const overrideTemplates = globalTemplates?.data ?? [];

  // Prepare events for the dropdown
  const systemEvents = (eventsData?.events || []).filter(
    event =>
      event.event !== 'AdhocEmailSent' &&
      !event.event.toLowerCase().includes('reminder')
  );

  // Preview modal mutation
  const previewMutation = useMutation({
    mutationFn: getEmailTemplatePreview,
    onSuccess: data => {
      setPreviewTemplate(data.renderedEmail);
      setOpenPreviewEmail(true);
    },
    onError: () => {
      throw new Error(`Something went wrong for preview`);
    }
  });

  const fetchPreview = async () => {
    const currentFormData = getValues();
    const { to, cc, bcc, ...restOfData } = currentFormData;

    const template = {
      ...restOfData,
      recipients: {
        to: extractEmailsFromTags(to),
        cc: extractEmailsFromTags(cc),
        bcc: extractEmailsFromTags(bcc)
      }
    };

    await previewMutation.mutateAsync({ template, event: {}, test: true });
  };

  const adhocEmailEvent = (eventsData?.events || []).filter(
    event => event.event === 'AdhocEmailSent'
  );

  const reminderEvents = (eventsData?.events || []).filter(({ event }) =>
    event.toLowerCase().includes('reminder')
  );
  const eventType = (() => {
    if (templateDetails) {
      const eventName = templateDetails.event;

      if (systemEvents.some(e => e.eventKey === eventName)) {
        return 'SystemEvents';
      }

      if (adhocEmailEvent.some(e => e.eventKey === eventName)) {
        return 'AdhocEmailSent';
      }

      if (reminderEvents.some(e => e.eventKey === eventName)) {
        return 'Reminder';
      }

      return 'unknown';
    }
  })();

  const isCustomizable = systemEvents?.find(
    ev => ev.eventKey === eventValue
  )?.personalizable;

  const apiLoadedEvent = useRef<{ event: string; state: string }>({
    event: '',
    state: ''
  });

  const overrideTemplateIdRef = useRef<string>('');

  // Reset form when template details load
  useEffect(() => {
    if (templateDetails && !isCreating) {
      if (templateDetails.scope !== 'global')
        overrideTemplateIdRef.current = templateDetails.templateId ?? '';

      reset({
        ...templateDetails,
        name: isEmailTemplateClone ? '' : templateDetails.name,
        to: templateDetails.recipients.to.map(email => ({ text: email })),
        cc: (templateDetails.recipients?.cc ?? []).map(email => ({
          text: email
        })),
        bcc: (templateDetails.recipients?.bcc ?? []).map(email => ({
          text: email
        })),
        trigger: eventType,
        reminderRound: templateDetails.reminderRound?.map(round =>
          round.toString()
        )
      });

      apiLoadedEvent.current = {
        event: templateDetails.event ?? '',
        state: templateDetails.state ?? ''
      };

      if (
        templateDetails?.event?.toLowerCase().includes('reminder') &&
        templateDetails.scope === 'journal' &&
        templateDetails.submissionContextId
      ) {
        const submissionContextId = templateDetails.submissionContextId[0]!;
        setSelectedSubmissionCtxId(submissionContextId);
      }
    } else if (isCreating) {
      reset(defaultValues);
      apiLoadedEvent.current = {
        event: '',
        state: ''
      };
    }
  }, [templateDetails, reset, isEmailTemplateClone, isCreating, isOpen]);

  useEffect(() => {
    if (!eventValue) return;

    const eventChanged = eventValue !== apiLoadedEvent.current.event;

    if (eventChanged) {
      setValue('state', '', { shouldValidate: false, shouldDirty: false });
      setValue('override', '', {
        shouldValidate: true,
        shouldDirty: true
      });
      overrideTemplateIdRef.current = '';
      return;
    }

    setValue('state', apiLoadedEvent.current.state, {
      shouldValidate: false,
      shouldDirty: false
    });
  }, [eventValue]);

  const creatingAndCloningState = isCreating || isEmailTemplateClone;

  const editTemplateMutation = useMutation({
    mutationFn: (data: ICreateTemplatePayload | IUpdateTemplatePayload) =>
      creatingAndCloningState
        ? createEmailTemplate(organizationId, data as ICreateTemplatePayload)
        : editEmailTemplateById(
            templateId,
            organizationId,
            data as IUpdateTemplatePayload
          ),
    onSuccess: (responseData: any, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['email-templates']
      });

      if (creatingAndCloningState) {
        setCreatedTemplateInfo({
          name: variables.name!,
          templateId:
            responseData?.templateId ||
            variables.templateId ||
            responseData?._id ||
            'N/A'
        });
      } else {
        toast({
          variant: 'success',
          title: 'Template updated successfully',
          duration: 3000,
          isClosable: true
        });

        queryClient.invalidateQueries({
          queryKey: ['email-template-details', templateId, organizationId]
        });

        onSuccess();
        onClose();
      }
    },
    onError: error => {
      const title = getErrorTitle(isCreating, error);
      toast({
        variant: 'error',
        title,
        duration: 5000,
        isClosable: true
      });
    }
  });

  const scrollToFirstValidationError = (errorFields: string[]) => {
    if (!errorFields.length) return;

    requestAnimationFrame(() => {
      if (!formRef.current) return;

      const allValidationNodes = Array.from(
        formRef.current.querySelectorAll<HTMLElement>('[data-validation-field]')
      );

      const target = allValidationNodes.find(node => {
        const fieldName = node.getAttribute('data-validation-field');
        return fieldName && errorFields.includes(fieldName);
      });

      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });

      setTimeout(() => {
        const focusTarget = target.matches(
          'input, select, textarea, button, [tabindex]'
        )
          ? target
          : target.querySelector<HTMLElement>(
              'input, select, textarea, button, [tabindex]'
            );

        focusTarget?.focus({ preventScroll: true });
      }, 500);
    });
  };

  const onInvalidSubmit = (validationErrors: FieldErrors<TemplateCreate>) => {
    scrollToFirstValidationError(Object.keys(validationErrors));
  };

  const setTemplateValidationErrors = (
    validation: TemplateValidationResult
  ) => {
    const errors: { name: string; unknowns: string[] }[] = [];

    // Top-level fields
    (['subject', 'body'] as const).forEach(field => {
      const fieldValidation = validation[field];
      if (
        !fieldValidation.isValid &&
        fieldValidation.unknownVariables?.length
      ) {
        errors.push({
          name: field,
          unknowns: fieldValidation.unknownVariables
        });
      }
    });

    // Recipients subfields
    (['to', 'cc', 'bcc'] as const).forEach(recipientType => {
      const fieldValidation = validation.recipients[recipientType];
      if (
        fieldValidation &&
        !fieldValidation.isValid &&
        fieldValidation.unknownVariables?.length
      ) {
        errors.push({
          name: recipientType,
          unknowns: fieldValidation.unknownVariables
        });
      }
    });

    // Set errors using setError
    errors.forEach(({ name, unknowns }) => {
      setError(name as keyof typeof defaultValues, {
        type: 'manual',
        message: `Unknown Variables: ${unknowns.join(', ')}`
      });
    });

    if (errors.length > 0) {
      scrollToFirstValidationError(errors.map(error => error.name));
    }

    return errors.length === 0; // Return true if all valid
  };

  const handleCloseStatusModal = () => {
    setIsOpenStatusModal(false);
    setPendingStatus(null);
  };

  const handleStatusChanges = () => {
    if (pendingStatus !== null) {
      setValue('active', pendingStatus, {
        shouldDirty: true,
        shouldValidate: true
      });
    }
    handleCloseStatusModal();
  };

  const onSubmit = async (data: TemplateCreate) => {
    const {
      to,
      cc,
      bcc,
      reminderRound: reminderRoundFormData,
      event,
      customizable,
      name,
      ...rest
    } = data;
    const normalizedEvent =
      triggerValue === 'AdhocEmailSent' ? triggerValue : event;
    const normalizedTag =
      normalizedEvent === 'EditorialSubmissionRejected'
        ? undefined
        : rest.tag?.trim() || undefined;
    const normalizedTemplateId =
      data.scope === 'global'
        ? undefined
        : overrideTemplateIdRef.current || undefined;

    const template: ITemplate = {
      ...rest,
      templateId: normalizedTemplateId,
      customizable,
      name: name.trim(),
      event: normalizedEvent as EventName,
      organizationId,
      recipients: {
        to: to ? to.map(recipient => recipient.text) : [],
        cc: cc ? cc.map(recipient => recipient.text) : [],
        bcc: bcc ? bcc.map(recipient => recipient.text) : []
      },
      tag: normalizedTag,
      submissionContextId:
        data.scope === 'global' ? [] : data.submissionContextId,
      reminderRound: reminderRoundFormData?.map(Number) || [],
      phases: data.phases ?? [],
      subject: data.subject,
      body: data.body,
      _id: ''
    };

    const validation = await validateEmailTemplate(
      template.organizationId,
      template
    );
    const isValidTemplate = setTemplateValidationErrors(validation);
    if (!isValidTemplate) return;
    const createPayload: ICreateTemplatePayload = {
      to: to ? to.map(recipient => recipient.text) : [],
      cc: cc ? cc.map(recipient => recipient.text) : [],
      bcc: bcc ? bcc.map(recipient => recipient.text) : [],
      organizationId: organizationId,
      name: name.trim(),
      event: triggerValue === 'AdhocEmailSent' ? triggerValue : event,
      customizable: triggerValue === 'AdhocEmailSent' ? true : customizable,
      reminderRound: reminderRoundFormData?.map(round => parseInt(round)) ?? [],
      tag: normalizedTag,
      ...rest,
      submissionContextId:
        data.scope === 'global' ? [] : data.submissionContextId,
      templateId: normalizedTemplateId
    };
    if (isCreating) {
      editTemplateMutation.mutate(createPayload);
    } else {
      const {
        to,
        cc,
        bcc,
        reminderRound: reminderRoundFormData,
        event,
        name,
        default: isDefault,
        customizable,
        ...rest
      } = data;
      const updatePayload: IUpdateTemplatePayload = {
        to: to ? to.map(recipient => recipient.text) : [],
        cc: cc ? cc.map(recipient => recipient.text) : [],
        bcc: bcc ? bcc.map(recipient => recipient.text) : [],
        name: name.trim(),
        event: triggerValue === 'AdhocEmailSent' ? triggerValue : event,
        customizable: triggerValue === 'AdhocEmailSent' ? true : customizable,
        default: isEmailTemplateClone ? false : isDefault,
        reminderRound:
          reminderRoundFormData?.map(round => parseInt(round)) ?? [],
        ...rest,
        tag: normalizedTag,
        submissionContextId:
          data.scope === 'global' ? [] : data.submissionContextId,
        templateId: normalizedTemplateId
      };
      editTemplateMutation.mutate(updatePayload);
    }
  };

  const reminderRoundsAndDaysConfig = filterAndGetReminderRounds(
    eventValue,
    stateValue,
    reminderConfigs
  );

  const reminderRoundsAndDaysConfigRef = useRef(reminderRoundsAndDaysConfig);

  useEffect(() => {
    reminderRoundsAndDaysConfigRef.current = reminderRoundsAndDaysConfig;
  }, [
    JSON.stringify(reminderRoundsAndDaysConfig.reminderRounds),
    JSON.stringify(reminderRoundsAndDaysConfig.reminderDays)
  ]);

  const filterReminderType = (config: IRemindersJournalConfigRouteResponse) => {
    const normalizedEvent = eventValue?.toLowerCase() ?? '';
    const normalizedType = config.type?.toLowerCase() ?? '';

    if (!normalizedEvent || !normalizedType) return false;

    return normalizedEvent.includes(normalizedType);
  };

  const updateReminderConfigMutation = useUpdateReminderConfigsByJournalId(
    organizationId,
    selectedSubmissionCtxId
  );

  const onCycleUpdateSubmit = async (roundsData: IAddReminderCycleFormType) => {
    if (!selectedSubmissionCtxId && !stateValue) {
      toast({
        variant: 'error',
        title:
          'Please select both Journal and Reminder Task before configuring reminder cycles'
      });
      return;
    }

    if (!selectedSubmissionCtxId) {
      toast({
        variant: 'error',
        title: 'Please select a Journal'
      });
      return;
    }

    if (!stateValue) {
      toast({
        variant: 'error',
        title: 'Please select a Reminder Task'
      });
      return;
    }

    const payload = {
      reminderDays: roundsData.reminderDays.map(day => parseInt(day)),
      workflowState: stateValue,
      workflowType: eventValue as TransitionWorkflowType
    };

    const response = await updateReminderConfigMutation.mutateAsync(payload);

    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Reminder Config updated successfully'
      });
    } else {
      const errors = (response as any)?.message?.errors;
      const title =
        Array.isArray(errors) && errors.length > 0
          ? errors.map((e: { message: string }) => e.message).join(', ')
          : 'Failed to update reminder days';

      toast({
        variant: 'error',
        title
      });
    }
  };

  const handleEditorChange = (content: string): void => {
    setValue('body', content, { shouldValidate: true });
  };

  const handleCloneTemplateFromEditView = () => {
    setIsClone!(true);
  };

  const handleOpenDeleteTemplateModal = () => {
    setIsDeleteTemplateModalOpen(true);
  };

  const handleCloseDeleteTemplateModal = () => {
    setIsDeleteTemplateModalOpen(false);
  };

  const handleDeleteTemplateSuccess = () => {
    onSuccess();
    onClose();
  };

  // Mutation method to validate default template
  const defaltTemplateValidation = useMutation({
    mutationFn: validateDefaultTemplate,
    onSuccess: data => {
      if (data.isValid) {
        editDefaultTemplateMutation.mutate({
          templateId: templateDetails!._id,
          organizationId: templateDetails!.organizationId,
          event: templateDetails!.event,
          tag: templateDetails!.tag
        });
      } else {
        templateNameAndEvent.current = {
          name: data.name ?? '',
          event: templateDetails!.event ?? ''
        };
        setShowDefaultTemplateConfirmationModal(true);
      }
    },
    onError: err => {
      toast({
        variant: 'error',
        title: 'Failed to update default template',
        description: String(err),
        duration: 5000,
        isClosable: true
      });
    }
  });

  // ValidateDefaultTemplate
  const handleValidateDefaultTemplate = (
    organizationId: string,
    event: string
  ) => {
    const data: IValidateDefaultTemplate = {
      organizationId,
      validateData: {
        event,
        default: true
      }
    };

    defaltTemplateValidation.mutate(data);
  };

  // Calling Edit template API to make the template default
  const editDefaultTemplateMutation = useMutation({
    mutationFn: ({
      templateId,
      organizationId,
      event,
      tag,
      customizable
    }: {
      templateId: string;
      organizationId: string;
      event: string;
      tag?: string;
      customizable?: boolean;
    }) =>
      editEmailTemplateById(templateId, organizationId, {
        default: true,
        event,
        active: true,
        tag,
        customizable
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Default template updated',
        duration: 3000,
        isClosable: true
      });

      if (templateNameAndEvent.current.name)
        setShowDefaultTemplateConfirmationModal(false);

      templateNameAndEvent.current = defaultTemplatenameAndEvent;

      queryClient.invalidateQueries({ queryKey: ['email-template-details'] });
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: err => {
      toast({
        variant: 'error',
        title: 'Failed to edit default template',
        description: String(err),
        duration: 5000,
        isClosable: true
      });
    }
  });

  const handleDefaultChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      handleValidateDefaultTemplate(
        organizationId,
        triggerValue === 'AdhocEmailSent' ? triggerValue : getValues('event')
      );
    }
  };

  const handleChangeDefaultTemplate = () => {
    editDefaultTemplateMutation.mutate({
      templateId: templateDetails!._id,
      organizationId,
      event:
        triggerValue === 'AdhocEmailSent' ? triggerValue : getValues('event'),
      tag: getValues('tag')?.trim() || undefined,
      customizable:
        triggerValue === 'AdhocEmailSent' ? true : getValues('customizable')
    });
  };

  const handleCloseDefaultTemplateConfirmationModal = () => {
    setValue('default', false, { shouldDirty: true, shouldValidate: true });

    setShowDefaultTemplateConfirmationModal(false);
  };

  const handleTemplateIdClick = () => {
    // 1. Update the URL query params
    setSearchParams({ search: createdTemplateInfo!.templateId });

    // 3. Close the modal overlays
    setCreatedTemplateInfo(null);
    onSuccess();
    onClose();
  };

  const handleCreateNewTemplate = () => {
    reset(defaultValues);
    setSelectedSubmissionCtxId('');
    setCreatedTemplateInfo(null);
    onSuccess();
  };

  const handleCopyAsNewTemplate = () => {
    reset({
      ...getValues(),
      name: ''
    });
    setCreatedTemplateInfo(null);
    onSuccess();
  };

  return (
    <>
      <Overlay height="100%">
        {addNewReminderDaysModalEnabled && (
          <AddReminderCycleModal
            isOpen={addNewReminderDaysModalEnabled}
            onClose={() => setAddNewReminderDaysModalEnabled(false)}
            onSubmit={onCycleUpdateSubmit}
            existingRoundDays={reminderRoundsAndDaysConfig.reminderDays}
          />
        )}

        {/* Default confirmation modal */}
        {showDefaultTemplateConfirmationModal && (
          <DefaultEmailTemplateModal
            isOpen={showDefaultTemplateConfirmationModal}
            onClose={handleCloseDefaultTemplateConfirmationModal}
            defaultEventDetail={templateNameAndEvent.current}
            onConfirmChange={handleChangeDefaultTemplate}
            loadingState={false}
          />
        )}

        {/* Status Action Confirmation Modal */}
        {isOpenStatusModal && (
          <StatusActionModal
            onClose={handleCloseStatusModal}
            isOpenStatusModal={isOpenStatusModal}
            onStatusChange={handleStatusChanges}
          />
        )}

        {/* Preview Modal */}
        {openPreviewEmail && (
          <Modal
            isOpen
            onClose={() => setOpenPreviewEmail(false)}
            width={{ base: 'calc(100vw - 16px)', md: '720px' } as any}
          >
            <Flex
              direction={'column'}
              gap={2}
              paddingX={{ base: 2, md: 4 }}
              paddingTop={4}
              paddingBottom={0}
            >
              <Text textStyle="headingGroup">Preview Email</Text>
              <Text textStyle="textContext">
                Displays the email using sample placeholder data for preview.
                Actual values are filled in when the email is triggered.
              </Text>
            </Flex>
            <Box margin={4}>
              <PreviewEmail template={previewTemplate} />
            </Box>
          </Modal>
        )}
        {isDeleteTemplateModalOpen && templateDetails?._id && (
          <DeleteTemplateModal
            templateId={templateDetails._id}
            organizationId={organizationId}
            isDefaultTemplate={!!templateDetails.default}
            onClose={handleCloseDeleteTemplateModal}
            onSuccess={onSuccess}
            onDeleteSuccess={handleDeleteTemplateSuccess}
          />
        )}
        <Flex
          flexDirection="column"
          backgroundColor="surfaceSecondaryFill"
          minH="100vh"
        >
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ base: 'flex-start', md: 'center' }}
            gap={4}
            mx={{ base: 4, md: 8 }}
            marginTop={{ base: 4, md: 8 }}
          >
            <Text textStyle="headingSection">
              {isCreating || isEmailTemplateClone
                ? 'Create New Template'
                : 'Edit Template'}
            </Text>
            {!(isCreating || isEmailTemplateClone) && (
              <Flex gap={3} flexWrap="wrap">
                <Button
                  variant="secondary"
                  onClick={handleCloneTemplateFromEditView}
                >
                  Copy as a New Template
                </Button>
                <Button
                  variant="caution"
                  onClick={handleOpenDeleteTemplateModal}
                >
                  Delete Template
                </Button>
              </Flex>
            )}
          </Flex>
          <form
            ref={formRef}
            onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
          >
            <Flex flexDirection={{ base: 'column', md: 'row' }}>
              {/* LEFT SECTION: Two Blocks */}
              <Flex
                flexDirection="column"
                gap={6}
                width={{ base: '100%', md: '70%' }}
                margin={{ base: 4, md: 8 }}
              >
                {/* Box 1: Email Configuration */}
                <Flex
                  flexDirection="column"
                  gap={4}
                  backgroundColor="surfacePrimaryFill"
                  padding={{ base: 4, md: 8 }}
                  borderRadius="md"
                >
                  <Text textStyle="headingSection">Email Configuration</Text>

                  {!(isCreating || isEmailTemplateClone) && (
                    <Flex
                      flexDirection={{ base: 'column', md: 'row' }}
                      gap={6}
                      width="100%"
                    >
                      <Flex flexDirection="column" gap={3} flex={1}>
                        <FieldLabel
                          label="Template ID"
                          subText="Unique template identifier."
                          isRequired={false}
                        />
                        <Input
                          name="templateId"
                          value={
                            templateDetails?.templateId ?? templateId ?? ''
                          }
                          disabled
                        />
                      </Flex>

                      {templateDetails?.scope !== 'global' && (
                        <Flex flexDirection="column" gap={3} flex={1}>
                          <FieldLabel
                            label="Journal Sequence ID"
                            subText="Unique journal sequence identifier."
                            isRequired={false}
                          />
                          <Input
                            name="journalSeqId"
                            value={templateDetails?.journalSeqId ?? ''}
                            disabled
                          />
                        </Flex>
                      )}
                    </Flex>
                  )}

                  <Flex
                    data-validation-field="name"
                    flexDirection="column"
                    gap={3}
                  >
                    <FieldLabel label="Title" subText="" isRequired />
                    <Input
                      {...register('name', {
                        required: 'Title is required'
                      })}
                      placeholder="Enter template title"
                      invalid={!!errors.name}
                    />
                    {errors.name && (
                      <FormFeedback variant="error">
                        {errors.name.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex flexDirection="column" gap={3}>
                    <FieldLabel
                      label="Description"
                      subText="Provide description for the template."
                      isRequired={false}
                    />
                    <TextArea
                      {...register('description', {
                        maxLength: {
                          value: 100,
                          message:
                            'Description character length should be less than 100'
                        }
                      })}
                      placeholder="Enter description"
                      characterLimit={100}
                      value={descriptionValue ?? ''}
                      isInvalid={Boolean(errors.description)}
                      errorMessage={
                        !!errors.description && errors.description.message
                      }
                    />
                  </Flex>

                  <Flex
                    data-validation-field="trigger"
                    flexDirection="column"
                    gap={3}
                  >
                    <FieldLabel label="Trigger Type" subText="" isRequired />
                    <Select
                      {...register('trigger', {
                        required: 'Trigger type is required'
                      })}
                      onChange={e => {
                        unregister('event');
                        setValue('trigger', e.target.value);
                        resetField('event');
                        clearErrors('event');
                      }}
                      isDisabled={!isCreating}
                    >
                      {Object.entries(triggerTypeLabelMapping).map(
                        ([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        )
                      )}
                    </Select>
                    {errors.trigger && (
                      <FormFeedback variant="error">
                        {errors.trigger.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex
                    flexDirection="column"
                    gap={3}
                    data-validation-field="event"
                  >
                    {(triggerValue === 'SystemEvents' ||
                      triggerValue === 'Reminder') && (
                      <FieldLabel
                        label={
                          triggerValue === 'SystemEvents'
                            ? 'System Events'
                            : 'Reminder Events'
                        }
                        subText=""
                        isRequired={
                          triggerValue === 'SystemEvents' ||
                          triggerValue === 'Reminder'
                        }
                      />
                    )}
                    {triggerValue === 'SystemEvents' && (
                      <Controller
                        name="event"
                        control={control}
                        rules={{ required: 'System event is required' }}
                        render={({ field }) => (
                          <FilterableSelect
                            options={systemEvents
                              .sort((a, b) =>
                                a.eventDisplayName.localeCompare(
                                  b.eventDisplayName
                                )
                              )
                              .map(e => ({
                                value: e.eventKey,
                                label: e.eventDisplayName
                              }))}
                            value={field.value}
                            onValueChange={value => {
                              field.onChange(value);
                              clearErrors('event');
                              if (!getValues('default')) {
                                setValue('customizable', false, {
                                  shouldValidate: true
                                });
                              }
                            }}
                            placeholder="Select System Events"
                            searchPlaceholder="Search system events"
                          />
                        )}
                      />
                    )}
                    {triggerValue === 'Reminder' && (
                      <Select
                        {...register('event', {
                          required: 'Reminder event is required'
                        })}
                      >
                        <option value="" disabled selected>
                          Select Event
                        </option>
                        {reminderEvents.map(event => (
                          <option key={event.eventKey} value={event.eventKey}>
                            {event.eventDisplayName}
                          </option>
                        ))}
                      </Select>
                    )}
                    {errors.event && (
                      <FormFeedback variant="error">
                        {errors.event.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex
                    data-validation-field="scope"
                    flexDirection="column"
                    gap={3}
                  >
                    <FieldLabel label="Scope" subText="" isRequired />
                    <Select
                      {...register('scope', {
                        required: 'Scope is required'
                      })}
                      onChange={e => {
                        const selectedValue = e.target.value;

                        setValue('scope', selectedValue as EmailScope);

                        if (triggerValue === 'Reminder') {
                          setValue('state', '', { shouldValidate: false });
                          setValue('reminderRound', [], {
                            shouldValidate: false
                          });
                        }
                      }}
                    >
                      <option value="" disabled selected>
                        Select Scope
                      </option>
                      <option value="global">Global/Workspace</option>
                      <option value="journal">Journal Specific</option>
                    </Select>
                    {errors.scope && (
                      <FormFeedback variant="error">
                        {errors.scope.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  {watch('scope') === 'journal' && (
                    <>
                      <Flex
                        flexDirection="column"
                        gap={3}
                        data-validation-field="submissionContextId"
                      >
                        <FieldLabel label="Journal" subText="" isRequired />
                        <Controller
                          control={control}
                          {...register('submissionContextId')}
                          rules={{
                            required: 'Journal is required'
                          }}
                          render={({ field }) =>
                            triggerValue === 'Reminder' ? (
                              <FilterableSelect
                                options={journalData.map(journal => ({
                                  value: journal.id,
                                  label: journal.data.name
                                }))}
                                value={field.value?.[0] || ''}
                                onValueChange={value => {
                                  field.onChange([value]);
                                  setSelectedSubmissionCtxId(value);
                                  setValue('reminderRound', []);
                                }}
                                placeholder="Select Journal"
                              />
                            ) : (
                              <MultiSelect
                                options={journalData.map(journal => ({
                                  value: journal.id,
                                  label: journal.data.name
                                }))}
                                value={field.value || []}
                                onChange={field.onChange}
                                placeholder="Select Journal"
                                variant="neutral"
                              />
                            )
                          }
                        />
                        {errors.submissionContextId && (
                          <FormFeedback variant="error">
                            {errors.submissionContextId.message}
                          </FormFeedback>
                        )}
                      </Flex>
                    </>
                  )}

                  {triggerValue === 'Reminder' && (
                    <>
                      <Flex
                        flexDirection="column"
                        gap={3}
                        data-validation-field="state"
                      >
                        <FieldLabel
                          label="Reminder Task"
                          subText=""
                          isRequired
                        />
                        <Select
                          {...register('state', {
                            required: 'Task is required'
                          })}
                          onChange={e => {
                            setValue('state', e.target.value, {
                              shouldValidate: true,
                              shouldDirty: true
                            });
                            setValue('override', '', {
                              shouldValidate: true,
                              shouldDirty: true
                            });
                            overrideTemplateIdRef.current = '';
                          }}
                        >
                          <option value="" disabled selected>
                            Select Task
                          </option>
                          {reminderConfigs
                            ?.filter(filterReminderType)
                            ?.map(config => (
                              <option
                                key={config.type + config.phase}
                                value={config.phase}
                              >
                                {config.displayName}
                              </option>
                            ))}
                        </Select>
                        {errors?.state && (
                          <FormFeedback variant="error">
                            {errors.state.message}
                          </FormFeedback>
                        )}
                      </Flex>

                      <Flex
                        flexDirection="column"
                        gap={3}
                        data-validation-field="reminderRound"
                      >
                        <FormControl isInvalid={!!errors.reminderRound}>
                          <Flex marginBottom={4}>
                            <FieldLabel
                              label="Reminder Cycle"
                              subText="Choose whether the email should be sent before or after the associated event is scheduled."
                              helpText="Updating or saving this reminder cycle will also update all templates that use the same reminder task. Please review before proceeding"
                              isRequired
                            />
                          </Flex>

                          <Controller
                            control={control}
                            name="reminderRound"
                            rules={{
                              validate: value => {
                                const config =
                                  reminderRoundsAndDaysConfigRef.current;
                                const currentOptions =
                                  config.reminderRounds?.map(round => ({
                                    value: round?.toString()
                                  })) ?? [];

                                const displayValue = (value ?? [])
                                  .map(v => v?.toString())
                                  .filter(v => v !== null && v !== undefined)
                                  .filter(v =>
                                    currentOptions.some(opt => opt.value === v)
                                  );

                                return (
                                  displayValue.length > 0 ||
                                  'Reminder Cycle is required'
                                );
                              }
                            }}
                            render={({ field, fieldState }) => {
                              const isJournal = scopeValue === 'journal';
                              const canShowReminderCycles =
                                !isJournal || Boolean(selectedJournalId);

                              const getDirection = (signedDays: number) =>
                                signedDays < 0
                                  ? { colorScheme: 'blue', label: 'Before' }
                                  : signedDays === 0
                                  ? { colorScheme: 'gray', label: 'On Day' }
                                  : { colorScheme: 'orange', label: 'After' };

                              const getSignedDays = (index: number): number =>
                                isJournal
                                  ? reminderRoundsAndDaysConfig.reminderDays?.[
                                      index
                                    ] ?? 0
                                  : reminderRoundsAndDaysConfig
                                      .reminderCycleRounds?.[index] ?? 0;

                              // ── Build options ────────────────────────────────────────────────────

                              const currentOptions: MultiSelectOption[] =
                                canShowReminderCycles
                                  ? reminderRoundsAndDaysConfig.reminderRounds?.map(
                                      (round, _) => {
                                        const dropdownLabel = `${convertNumberToOrdinal(
                                          round
                                        )} cycle`;

                                        return {
                                          value: round?.toString(),
                                          label: dropdownLabel,
                                          // ① Plain cycle label as the chip — no badge, no styled pill
                                          displayLabel: dropdownLabel
                                        };
                                      }
                                    ) ?? []
                                  : [];

                              // ── Value helpers ────────────────────────────────────────────────────

                              const fullValue = (field.value ?? [])
                                .map(v => v?.toString())
                                .filter(v => v !== null && v !== undefined);

                              const displayValue = fullValue.filter(v =>
                                currentOptions.some(opt => opt.value === v)
                              );

                              const otherValues = fullValue.filter(
                                v =>
                                  !currentOptions.some(opt => opt.value === v)
                              );

                              const handleChange = (newValues: string[]) => {
                                const updatedValues = [
                                  ...otherValues,
                                  ...newValues
                                ];

                                setValue('reminderRound', updatedValues, {
                                  shouldDirty: true,
                                  shouldValidate: true
                                });

                                if (newValues.length > 0) {
                                  clearErrors('reminderRound');
                                }
                              };

                              // ── Custom option row ────────────────────────────────────────────────

                              const renderOption = ({
                                option,
                                isSelected
                              }: {
                                option: MultiSelectOption;
                                isSelected: boolean;
                              }) => {
                                const index =
                                  reminderRoundsAndDaysConfig.reminderRounds?.findIndex(
                                    r => r?.toString() === option.value
                                  );
                                const signedDays = getSignedDays(index ?? -1);
                                const absDays = Math.abs(signedDays);
                                const direction = getDirection(signedDays);

                                return (
                                  <Flex
                                    align="center"
                                    justify="space-between"
                                    w="full"
                                    gap={3}
                                  >
                                    <Box pointerEvents="none">
                                      <Checkbox
                                        isChecked={isSelected}
                                        ariaLabel={option.value}
                                        id={option.value}
                                        name={option.label}
                                      >
                                        {option.label}
                                      </Checkbox>
                                    </Box>

                                    <Grid
                                      templateColumns="55px 70px"
                                      flexShrink={0}
                                      alignItems="center"
                                    >
                                      <Text
                                        fontSize="13px"
                                        fontWeight="semibold"
                                        textAlign="right"
                                        whiteSpace="nowrap"
                                        pr={2}
                                      >
                                        {absDays}
                                        <Text
                                          as="span"
                                          fontSize="12px"
                                          fontWeight="normal"
                                          ml={0.5}
                                        >
                                          {absDays === 1 ? 'day' : 'days'}
                                        </Text>
                                      </Text>

                                      <Box>
                                        <Badge
                                          colorScheme={direction.colorScheme}
                                          borderRadius="full"
                                          fontSize="11px"
                                          px={2}
                                          py={0.5}
                                          textTransform="capitalize"
                                        >
                                          {direction.label}
                                        </Badge>
                                      </Box>
                                    </Grid>
                                  </Flex>
                                );
                              };

                              return (
                                <FormControl isInvalid={!!fieldState.error}>
                                  <ComposableMultiSelect
                                    options={currentOptions}
                                    value={displayValue}
                                    onChange={handleChange}
                                    placeholder="Select reminder options"
                                    variant="interactive"
                                    renderOption={renderOption}
                                    customButtonText={
                                      canShowReminderCycles &&
                                      reminderRoundsAndDaysConfig.reminderDays &&
                                      scopeValue !== 'global' ? (
                                        <ManageCyclesButton />
                                      ) : undefined
                                    }
                                    customButtonOnClickFunction={
                                      canShowReminderCycles &&
                                      reminderRoundsAndDaysConfig.reminderDays
                                        ? () =>
                                            setAddNewReminderDaysModalEnabled(
                                              true
                                            )
                                        : undefined
                                    }
                                  />
                                </FormControl>
                              );
                            }}
                          />
                        </FormControl>
                        {errors?.reminderRound && (
                          <FormFeedback variant="error">
                            {errors.reminderRound.message}
                          </FormFeedback>
                        )}
                      </Flex>
                    </>
                  )}

                  {watch('scope') === 'journal' && (
                    <Flex
                      flexDirection="column"
                      gap={3}
                      data-validation-field="override"
                    >
                      <FieldLabel
                        label="Overrides Global Template"
                        subText="Choose a global template that you would like to replace with the new template you are creating."
                        isRequired
                      />
                      <Select
                        {...register('override', {
                          required: 'Overrides Global Template is required'
                        })}
                        onChange={e => {
                          const selectedId = e.target.value;

                          setValue('override', selectedId, {
                            shouldValidate: true,
                            shouldDirty: true
                          });

                          const selectedOverrideTemplate =
                            overrideTemplates.find(
                              template => template._id === selectedId
                            );

                          overrideTemplateIdRef.current =
                            selectedOverrideTemplate?.templateId ?? '';
                        }}
                        invalid={!!errors.override}
                      >
                        <option value="" disabled selected>
                          Select Overrides Global Template
                        </option>
                        {overrideTemplates
                          .filter(template => template.name !== watch('name'))
                          .map(template => (
                            <option key={template._id} value={template._id}>
                              {template.name}
                            </option>
                          ))}
                      </Select>
                      {errors.override && (
                        <FormFeedback variant="error">
                          {errors.override.message}
                        </FormFeedback>
                      )}
                    </Flex>
                  )}

                  <Flex flexDirection="column" gap={3}>
                    <FieldLabel
                      label="Template Status"
                      subText=""
                      isRequired={false}
                    />
                    <Flex alignItems="center" gap={2}>
                      <Controller
                        name="active"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Switch
                              key={`active-${templateDetails?._id}-${templateDetails?.active}`}
                              id="active"
                              isChecked={field.value}
                              onChange={e => {
                                const checked = e.target.checked;

                                if (checked) {
                                  field.onChange(true);
                                  return;
                                }

                                if (watch('default') && !checked) {
                                  toast({
                                    variant: 'alert',
                                    title:
                                      'Default template cannot be disabled',
                                    duration: 5000,
                                    isClosable: true
                                  });
                                  field.onChange(true);
                                  return;
                                }

                                setPendingStatus(checked);
                                setIsOpenStatusModal(true);
                              }}
                            />
                            <FormLabel
                              htmlFor="active"
                              mb="0"
                              textStyle="textBody"
                              cursor="pointer"
                            >
                              {field.value ? 'Active' : 'Inactive'}
                            </FormLabel>
                          </>
                        )}
                      />
                    </Flex>
                  </Flex>

                  {!(isCreating || isEmailTemplateClone) &&
                    customizable === true && (
                      <Flex flexDirection="column" gap={3}>
                        <FieldLabel
                          label="Default Template Setting"
                          subText=""
                          isRequired={false}
                        />
                        <Flex alignItems="center" gap={2}>
                          <Controller
                            name="default"
                            control={control}
                            render={({ field }) => (
                              <Switch
                                id="makeAsDefault"
                                isChecked={field.value}
                                isDisabled={templateDetails?.default}
                                onChange={e => {
                                  field.onChange(e.target.checked);
                                  if (!(isCreating || isEmailTemplateClone)) {
                                    handleDefaultChange(e);
                                  }
                                }}
                              />
                            )}
                          />
                          <FormLabel
                            htmlFor="makeAsDefault"
                            mb="0"
                            textStyle="textBody"
                          >
                            Make as default
                          </FormLabel>
                        </Flex>
                      </Flex>
                    )}

                  {triggerValue !== 'Reminder' && (
                    <Flex flexDirection="column" gap={3}>
                      <FieldLabel
                        label="Template Configurable Setting"
                        subText=""
                        isRequired={false}
                      />
                      <Controller
                        name="customizable"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            ariaLabel={''}
                            name="customizable"
                            id="customizable"
                            isChecked={
                              triggerValue === 'AdhocEmailSent' || field.value
                            }
                            isDisabled={
                              triggerValue === 'AdhocEmailSent' ||
                              !isCustomizable ||
                              watch('default')
                            }
                            onChange={e => field.onChange(e.target.checked)}
                          >
                            {!isCustomizable &&
                            triggerValue !== 'AdhocEmailSent' ? (
                              <Tooltip
                                hasArrow
                                borderRadius={4}
                                placement="top"
                                label="This system event does not support customizable e-mail templates."
                              >
                                Allow editors to configure this template
                              </Tooltip>
                            ) : (
                              <>Allow editors to configure this template</>
                            )}
                          </Checkbox>
                        )}
                      />
                    </Flex>
                  )}

                  {triggerValue !== 'AdhocEmailSent' && (
                    <Flex flexDirection="column" gap={3}>
                      <FormControl
                        isInvalid={!!errors.phases}
                        data-validation-field="phases"
                      >
                        <Flex marginBottom={4}>
                          <FieldLabel label="Workflow" subText="" isRequired />
                        </Flex>
                        <Controller
                          control={control}
                          {...register('phases')}
                          rules={{
                            required: 'Workflow is required'
                          }}
                          render={({ field: { ref: _ref, ...rest } }) => (
                            <MultiSelect
                              options={[
                                {
                                  value: 'sub',
                                  label: 'Original Submission'
                                },
                                { value: 'resub', label: 'Resubmission' },
                                { value: 'rev', label: 'Revision' }
                              ]}
                              value={rest.value || []}
                              onChange={rest.onChange}
                              placeholder="Select Workflow"
                              variant="interactive"
                            />
                          )}
                        />
                      </FormControl>
                    </Flex>
                  )}

                  <Flex flexDirection="column" gap={3}>
                    <FieldLabel
                      label="Email Tag"
                      subText=""
                      isRequired={false}
                    />
                    <Select {...register('category')}>
                      <option value="" selected>
                        Select Email Tag
                      </option>
                      {categories &&
                        categories.map(category => (
                          <option key={category._id} value={category.name}>
                            {category.name}
                          </option>
                        ))}
                    </Select>
                  </Flex>
                </Flex>

                {/* Box 2: Email */}
                <Flex
                  flexDirection="column"
                  gap={4}
                  backgroundColor="surfacePrimaryFill"
                  padding={{ base: 4, md: 8 }}
                  borderRadius="md"
                >
                  <Text textStyle="headingSection">Email</Text>

                  <Flex
                    data-validation-field="to"
                    flexDirection="column"
                    gap={3}
                  >
                    <FieldLabel label="To" subText="" isRequired />
                    <Controller
                      name="to"
                      control={control}
                      rules={{
                        validate: value => value?.length > 0 || 'To is required'
                      }}
                      render={({ field: _field }) => (
                        <TagInput
                          name="to"
                          control={control}
                          placeholder="Enter text here"
                        />
                      )}
                    />
                    {errors.to && (
                      <FormFeedback variant="error">
                        {errors.to.root?.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex flexDirection="column" gap={3}>
                    <FieldLabel label="Cc" subText="" isRequired={false} />
                    <TagInput
                      name="cc"
                      control={control}
                      placeholder="Enter text here"
                    />
                    {errors.cc && (
                      <FormFeedback variant="error">
                        {errors.cc.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex flexDirection="column" gap={3}>
                    <FieldLabel label="Bcc" subText="" isRequired={false} />
                    <TagInput
                      name="bcc"
                      control={control}
                      placeholder="Enter text here"
                    />
                    {errors.bcc && (
                      <FormFeedback variant="error">
                        {errors.bcc.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex
                    data-validation-field="subject"
                    flexDirection="column"
                    gap={3}
                  >
                    <FieldLabel label="Subject" subText="" isRequired />
                    <Input
                      {...register('subject', {
                        required: 'Subject is required'
                      })}
                      placeholder="Enter email subject"
                      invalid={!!errors.subject}
                    />
                    {errors.subject && (
                      <FormFeedback variant="error">
                        {errors.subject.message}
                      </FormFeedback>
                    )}
                  </Flex>

                  <Flex flexDirection="column" gap={3}>
                    <Flex
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                    >
                      <Box flex="1" minWidth={0}>
                        <FieldLabel
                          label="HTML email body"
                          subText={`Enter email content in HTML using placeholders in {{ }} format (e.g., {{Document_ID}}). Values are replaced with actual data when the email is triggered.`}
                          isRequired
                        />
                      </Box>
                      <Tooltip
                        label="Displays the email using sample placeholder data for preview. Actual values are filled in when the email is triggered"
                        placement="top"
                        hasArrow
                      >
                        <Box flexShrink={0}>
                          <Button
                            variant="secondary"
                            onClick={() => fetchPreview()}
                            disabled={previewMutation.isPending}
                            isLoading={previewMutation.isPending}
                            whiteSpace="nowrap"
                          >
                            Test Preview
                          </Button>
                        </Box>
                      </Tooltip>
                    </Flex>
                    <Box width="100%" data-validation-field="body">
                      <Box
                        className={errors.body ? 'editorErrorContainer' : ''}
                        width="100%"
                      >
                        <Editor
                          {...(apiKeyTinymce
                            ? { apiKey: apiKeyTinymce }
                            : { tinymceScriptSrc: selfHostedTinymce })}
                          licenseKey={selfHostedTinymce ? 'gpl' : ''}
                          value={watch('body')}
                          {...register('body', {
                            required: 'HTML Email Body is required.'
                          })}
                          init={{
                            menubar: false,
                            statusbar: false,
                            paste_as_text: true,
                            plugins: ['autoresize', 'image'],
                            toolbar:
                              'undo redo | formatselect | bold italic underline strikethrough | ' +
                              'alignleft aligncenter alignright alignjustify | ' +
                              'bullist numlist outdent indent | ' +
                              'link image table hr charmap | ' +
                              'code preview | math',
                            automatic_uploads: true,
                            file_picker_types: 'image',
                            entity_encoding: 'raw'
                          }}
                          onEditorChange={handleEditorChange}
                        />
                      </Box>

                      {errors.body && (
                        <FormFeedback variant="error">
                          {errors.body.message}
                        </FormFeedback>
                      )}
                    </Box>
                  </Flex>

                  {!isCreating && (
                    <Flex flexDirection="column">
                      {templateDetails?.createdBy?.name && (
                        <Text>{`Created by: ${
                          templateDetails.createdBy.name
                        } on ${dateFormat(
                          templateDetails?.createdAt,
                          "mmm dd, yyyy 'at' h:MM TT"
                        )}`}</Text>
                      )}
                      {templateDetails?.editedBy?.name && (
                        <Text>
                          {`Last Edited by: ${
                            templateDetails.editedBy.name
                          } on ${dateFormat(
                            templateDetails?.updatedAt,
                            "mmm dd, yyyy 'at' h:MM TT"
                          )}`}
                        </Text>
                      )}
                    </Flex>
                  )}
                </Flex>
              </Flex>

              {/* RIGHT SECTION: Sticky Personalized Variables */}
              <Flex
                width={{ base: '100%', md: '35%' }}
                margin={{ base: 4, md: 8 }}
                flexDirection="column"
                gap={6}
                position="sticky"
                top="24px"
                alignSelf="flex-start"
                backgroundColor="surfacePrimaryFill"
                padding={{ base: 4, md: 8 }}
                borderRadius="md"
              >
                <Flex flexDirection="column">
                  <Text textStyle="headingGroup">Personalized variables</Text>
                  <Text textStyle="textBody">
                    Personalize your email using the variables provided.
                  </Text>
                </Flex>
                <TemplateVariablesList />
                <Flex flexDirection="column" gap={4} mt={8}>
                  <FreshTestEmailContainer
                    control={control}
                    organizationId={organizationId}
                  />
                </Flex>
              </Flex>
            </Flex>

            <Flex
              padding={4}
              bottom={0}
              justifyContent="center"
              position="sticky"
              bgColor="surfacePrimaryFill"
            >
              <Flex width="100%" justifyContent="flex-end" gap={4}>
                <Button
                  variant="secondary"
                  isDisabled={editTemplateMutation.isPending}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={editTemplateMutation.isLoading}
                  isDisabled={
                    (isLoadingTemplate && !isCreating) ||
                    editTemplateMutation.isPending
                  }
                >
                  {isCreating ? 'Create Template' : 'Save Changes'}
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      </Overlay>

      {createdTemplateInfo && (
        <Overlay height="100%" zIndex={1500}>
          <TemplateFlowSuccessLayout width="720px">
            <Flex
              direction="column"
              align="center"
              justify="center"
              w="full"
              mt={10}
              px={4}
            >
              {/* Green Banner with Checkmark */}
              <Box
                w="full"
                background={'successSecondaryFill'}
                p={10}
                borderRadius="md"
                display="flex"
                justifyContent="center"
                alignItems="center"
                mb={8}
              >
                <FontAwesomeIcon fontSize={40} icon={faCircleCheck} />
              </Box>

              <Text textStyle="headingTitle" mb={2}>
                Template Created Successfully
              </Text>

              <Link
                variant="primary"
                fontSize="lg"
                color="blue.600"
                fontWeight="semibold"
                textDecoration="underline"
                mb={4}
                onClick={handleTemplateIdClick}
              >
                Template ID: {createdTemplateInfo.templateId}
              </Link>

              <Text textAlign="center" maxW="700px" mb={8}>
                Your email template{' '}
                <strong>"{createdTemplateInfo.name}"</strong> has been created
                successfully. You can now use this template to personalize
                communications across your workflow.
              </Text>

              <Flex gap={4}>
                <Button variant="secondary" onClick={handleCreateNewTemplate}>
                  Create New Template
                </Button>
                <Button variant="secondary" onClick={handleCopyAsNewTemplate}>
                  Copy as New Template
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setCreatedTemplateInfo(null);
                    onSuccess();
                    onClose();
                  }}
                >
                  View All Templates
                </Button>
              </Flex>
            </Flex>
          </TemplateFlowSuccessLayout>
        </Overlay>
      )}
    </>
  );
};

export default EditTemplateModal;
